import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  SimpleGrid,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { EmptyState, PageContainer, PageHeader, Skeleton } from 'components/ds';
import StateText from 'components/StateText';
import { CONNECTION_STATUS } from 'helpers/constant';
import useQueryParam from 'hooks/useQueryParam';
import { Link2, Plus } from 'lucide-react';
import useConnection, { SharepointConnection, useConfirmConnection } from 'queries/useConnection';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import AddConnectionModal from './AddConnectionModal';
import ConnectionActionButtons from './ConnectionActionButtons';

const SyncSharepoint = (): JSX.Element => {
  const query = useQueryParam();
  const { t } = useTranslation();
  const connectionId = query.get('connection_id');
  const {
    data: connections = [],
    isFetching,
    isFetched,
    isError,
  } = useConnection({ enabled: !connectionId });

  const history = useHistory();
  const { pathname } = useLocation();

  const { mutate: confirmConnection, isPending: isConfirming } = useConfirmConnection();

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

  useEffect(() => {
    if (connectionId) {
      confirmConnection(connectionId, {
        onSettled: () => {
          history.replace(pathname);
        },
      });
    }
  }, [connectionId, confirmConnection, pathname, history]);

  const isLoading = isFetching || isConfirming;
  const isEmpty = isFetched && !isError && !connections.length;

  return (
    <PageContainer>
      {/* Page header — shared DS component (left title + subtitle, right action) */}
      <PageHeader
        title={t('connection.sharePointConnections', { defaultValue: 'SharePoint connections' })}
        subtitle={t('connection.sharePointConnectionSubtitle', {
          defaultValue: 'Link document libraries so Amatrium can keep the knowledge base in sync.',
        })}
        action={
          <Button
            leftIcon={<Plus size={16} />}
            onClick={onCreateOpen}
            size='ds-md'
            variant='ds-primary'
          >
            {t('connection.addConnection', { defaultValue: 'Add connection' })}
          </Button>
        }
      />

      {/* Loading — skeleton cards mirroring grid (design-guidelines.md §7.2) */}
      {isLoading && (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <CardBody p={6}>
                <Flex justify='space-between' mb={4}>
                  <Skeleton w='40px' h='40px' borderRadius='md' />
                  <Skeleton w='80px' h='22px' borderRadius='sm' />
                </Flex>
                <Skeleton w='70%' h='18px' mb={2} />
                <Skeleton w='50%' h='14px' mb={6} />
                <Skeleton w='120px' h='32px' borderRadius='sm' />
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Error state */}
      {!isLoading && isError && (
        <EmptyState
          icon={<Link2 size={24} />}
          title={t('common.error', { defaultValue: 'Something went wrong' })}
          description={t('connection.loadError', {
            defaultValue: 'Could not load connections. Please try again.',
          })}
        />
      )}

      {/* Empty state */}
      {!isLoading && isEmpty && (
        <EmptyState
          icon={<Link2 size={24} />}
          title={t('connection.emptyTitle', { defaultValue: 'No connections yet' })}
          description={t('connection.emptyDescription', {
            defaultValue: 'Add a SharePoint connection to start syncing document libraries.',
          })}
          action={
            <Button
              leftIcon={<Plus size={16} />}
              onClick={onCreateOpen}
              size='ds-md'
              variant='ds-primary'
            >
              {t('connection.addConnection', { defaultValue: 'Add connection' })}
            </Button>
          }
        />
      )}

      {/* Status cards grid — mockup .stat-grid; reflows 1→2→3 cols so it never
          overflows on tablet/mobile (md=tablet stays 2-up; xl unlocks 3-up) */}
      {!isLoading && !isError && !isEmpty && (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {connections.map((c: SharepointConnection) => {
            const isConnected = c.status_code === CONNECTION_STATUS.CONNECTED;
            return (
              <Card key={c.id} variant='ds-hover'>
                <CardBody p={6}>
                  {/* Header row — icon chip + status badge (mockup .stat__icon + .badge) */}
                  <Flex align='center' justify='space-between' mb={4}>
                    <Flex
                      align='center'
                      justify='center'
                      w='40px'
                      h='40px'
                      color='primary.solid'
                      bg='primary.soft'
                      borderRadius='md'
                    >
                      <Link2 size={18} />
                    </Flex>
                    <Badge borderRadius='full' variant={isConnected ? 'ds-success' : 'ds-warning'}>
                      <Box as='span' w='6px' h='6px' mr={1} bg='currentColor' borderRadius='full' />
                      <StateText statusKey={c.status_code} />
                    </Badge>
                  </Flex>

                  <Text color='text.default' fontSize='md' fontWeight={600} noOfLines={1}>
                    {c.display_name}
                  </Text>
                  <Text mt='2px' color='text.muted' fontSize='sm' noOfLines={1}>
                    {c.authenticated_user || 'N/A'}
                  </Text>

                  {/* Action buttons — role/status wiring preserved verbatim */}
                  <Box mt={4}>
                    <ConnectionActionButtons info={c} />
                  </Box>
                </CardBody>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      <AddConnectionModal onClose={onCreateClose} isOpen={isCreateOpen} />
    </PageContainer>
  );
};

export default SyncSharepoint;
